import { AxiosError, isAxiosError } from 'axios'
import useNotification from '@/composables/notifications'
import { useI18n } from '@/composables/i18n'
import { isNil } from 'ramda'

const notifications = useNotification()

const getErrorCode = (e: unknown) => {
    const data = (e as AxiosError | undefined)?.response?.data as Record<string, string> | undefined

    if (Array.isArray(data?.errors)) {
        const nonIndexedMsgs = data.errors
            .filter(e => isNil(e.index))
            .map(e => e.code)
            .filter(Boolean)

        if (nonIndexedMsgs.length) {
            return nonIndexedMsgs.join(', ')
        }

        return [
            ...new Set(data.errors
                .filter(e => e.code)
                .map(e => e.code),
            ),
        ]
    }

    return data?.code
}

const getErrorStatus = (e: unknown) => {
    if (isAxiosError(e)) {
        return e.response?.status || 0
    }

    return 0
}

const isClientAxiosError = (e: unknown) => {
    const status = getErrorStatus(e)
    return status >= 400 && status < 500
}

const getErrorMessage = (e: unknown) => {
    const data = (e as AxiosError | undefined)?.response?.data as Record<string, string> | undefined

    if (Array.isArray(data?.errors)) {
        const nonIndexedMsgs = data.errors
            .filter(e => isNil(e.index))
            .map(e => e.message || e.code)
            .filter(Boolean)

        if (nonIndexedMsgs.length) {
            return nonIndexedMsgs.join(', ')
        }

        return [
            ...new Set(data.errors
                .filter(e => e.message || e.code)
                .map(e => `${e.message || e.code}`),
            ),
        ]
            .join(', ')
    }

    return data?.errorMessage || data?.message
}

const handleApiError = (e: unknown) => {
    const { t, te } = useI18n()
    const errorMessage = getErrorMessage(e as AxiosError)

    if (errorMessage) {
        notifications.error(te('errors.' + errorMessage) ? t('errors.' + errorMessage) : errorMessage)
        return
    }

    if (isAxiosError(e) && e.code === AxiosError.ERR_CANCELED) {
        return
    }

    console.error(e) // eslint-disable-line no-console
    notifications.error((e as Error).message || t('common.something-went-wrong'))
}

export default () => ({
    handleApiError,
    getErrorStatus,
    isClientAxiosError,
    getErrorMessage,
    getErrorCode,
})
